import React, { useEffect } from "react";
import { useLocation } from 'react-router-dom';
import { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import { jwtDecode } from "jwt-decode";
import { initGA, trackPageView, trackError, trackUserLogin } from './utils/analytics';

import App from "./pages/App";
import ClassDetail from "./pages/ClassDetail";
import CreateClass from "./pages/CreateClass";
import CreateSETClass from "./pages/CreateSETClass";
import CreateTrialClass from "./pages/CreateTrialClass";
import CreateSpecialEdClass from "./pages/CreateSpecialEdClass";
import ShareClass from "./pages/ShareClass";
import AddStudents from "./pages/AddStudents";
import ClassSettings from "./pages/ClassSettings";
import ClassSettingsSpecial from "./pages/ClassSettingsSpecial";
import StudentsResults from "./pages/StudentsResults";
import StudentDetails from "./pages/StudentDetails";
import StudentSettings from "./pages/StudentSettings";
import IndividualStudentResults from "./pages/IndividualStudentResults";
import AssignAssessment from "./pages/AssignAssessment";
import TeacherProfile from "./pages/TeacherProfile";
import Results from "./pages/Results";

import MasterDashboard from "./pages/MasterDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AssessmentsDashboard from "./pages/AssessmentsDashboard";
import ClassAdminDashboard from "./pages/ClassAdminDashboard";
import QRDashboard from "./pages/QRDashboard";
import ResultsDashboard from "./pages/ResultsDashboard";
import AssessmentDetail from "./pages/AssessmentDetail";
import ResultDetail from "./pages/ResultDetail";
import Sandbox from "./pages/Sandbox";
import SuperSecretInternalDash from "./pages/SuperSecretInternalDash";
import MyColleagues from "./pages/MyColleagues";
import InviteColleagues from "./pages/InviteColleagues";
import ReferFriends from "./pages/ReferFriends";
import Reports from "./pages/Reports";
import JiSiReports from "./pages/JiSiReports";
import SencoReports from "./pages/SencoReports";
import SiFcReports from "./pages/SiFcReports";
import RetestTaskDashboard from "./pages/RetestTaskDashboard";
import AssignTask from "./pages/AssignTask";
import RetestTaskDetail from "./pages/RetestTaskDetail";
import AllStudentResults from "./pages/AllStudentResults";
import AllStudentReportCards from "./pages/AllStudentReportCards";
import SencoResultDetail from "./pages/SencoResultDetail";
import SencoStudentResults from "./pages/SencoStudentResults";
import CustomAssessmentsDashboard from "./pages/CustomAssessmentsDashboard";
import HandoverDocs from "./pages/HandoverDocs";
import ClassroomHandoverSetup from "./pages/ClassroomHandoverSetup";
import ClassroomHandoverChildren from "./pages/ClassroomHandoverChildren";
import BeforeScreening from "./pages/BeforeScreening";
import DyslexiaScreenerDashboard from "./pages/DyslexiaScreenerDashboard";
import DyslexiaScreenerAdmin from "./pages/DyslexiaScreenerAdmin";
import DyslexiaScreenerResults from "./pages/DyslexiaScreenerResults";
import DyslexiaScreenerSession from "./pages/DyslexiaScreenerSession";
import DyslexiaScreenerForm from "./pages/DyslexiaScreenerForm";

import RTI from "./pages/RTI";
import AladdinExport from "./pages/AladdinExport";
import ScrollToTop from "./components/ScrollToTop";

import SENWatchlist from "./pages/SENWatchlist";

import Chatbot from "./pages/Chatbot";
import EOYReportAssistant from "./pages/EOYReportAssistant";
import EnhancedAIReports from "./pages/EnhancedAIReports";
import Reporting from "./pages/Reporting";
import Sensai from "./pages/Sensai";


import "./fonts/Adia_Demo.ttf";

import "./index.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import SignUp from "./pages/SignUp";
import Recovery from "./pages/Recovery";
import Recommendations from "./pages/Recommendations";
import userJourney from './utils/userJourneyLogger';
import { MessageModal } from "./components/MessageModal";
import ClassAccessDeniedAlert from "./components/ClassAccessDeniedAlert";
import ClassAccessFlowPreview from "./components/ClassAccessFlowPreview";
import {
  AladdinExportAvailabilityGate,
  AladdinExportAvailabilityProvider,
} from "./contexts/AladdinExportAvailabilityContext";
import {
  TeacherNavigationGate,
  TeacherNavigationProvider,
} from "./contexts/TeacherNavigationContext";
import {
  RoleSelectionProvider,
  useRoleSelection,
} from "./contexts/RoleSelectionContext";
import { TEACHER_DASHBOARD_NAVIGATION_IDS } from "./config/teacherDashboardNavigation";

const container = document.getElementById("root");
const root = createRoot(container);

function ProtectedRoute({ element, navigationId, navigationIds }) {
  const location = useLocation();
  const { loading: roleSelectionLoading, needsRoleSelection, teacherid } =
    useRoleSelection();
  const token = localStorage.getItem("token");
  const loginState =
    location.pathname === "/class/share"
      ? {
          returnTo: {
            pathname: location.pathname,
            search: location.search,
          },
        }
      : undefined;
  console.log("Checking token...");

  if (!token) {
    console.log("No token found, redirecting to home page.");
    return <Navigate to="/" replace state={loginState} />;
  }

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    if (decodedToken.exp < currentTime) {
      console.log("Token expired, removing token and redirecting to home page.");
      userJourney.logJWTExpiry();
      localStorage.removeItem("token");
      return <Navigate to="/" replace state={loginState} />;
    }

    console.log("Token is valid, rendering element.");

    if (roleSelectionLoading) {
      return (
        <div className="spinner-container">
          <div className="big-ball-spinner" />
        </div>
      );
    }

    if (needsRoleSelection && location.pathname !== "/profile") {
      return (
        <Navigate
          to="/profile"
          replace
          state={{ teacherid, profilePageRedirect: true }}
        />
      );
    }
  } catch (e) {
    console.error("Token decoding failed or invalid token, redirecting to home page.");
    trackError('Token Decode', e.message);
    userJourney.logSessionEnd('invalid_token');
    userJourney.flushLogs();
    localStorage.removeItem("token");
    return <Navigate to="/" replace state={loginState} />;
  }

  return navigationId || navigationIds ? (
    <TeacherNavigationGate navigationId={navigationId} navigationIds={navigationIds}>
      {element}
    </TeacherNavigationGate>
  ) : element;
}

function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;
    const pageName = currentPath.replace(/^\//, '') || 'home';
    trackPageView(currentPath + location.search);
    const pageStartTime = performance.now();
    const arrivalTime = new Date().toISOString();

    return () => {
      const timeOnPage = (performance.now() - pageStartTime) / 1000;
      if (timeOnPage > 0.5) {
        userJourney.logEvent('pagevisit', pageName, {
          timeInSeconds: Math.round(timeOnPage),
          arrivedAt: arrivalTime,
          leftAt: new Date().toISOString(),
          referrer: document.referrer,
          sessionId: userJourney.sessionId,
          screenSize: `${window.innerWidth}x${window.innerHeight}`
        }, currentPath);
      }
    };
  }, [location]);

  return null;
}

function RootComponent() {
  useEffect(() => {
    userJourney.init();
    initGA();
    trackPageView(window.location.pathname + window.location.search);

    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const userRole = decodedToken.role || 'Teacher';
        trackUserLogin(decodedToken.sub, userRole);
      } catch (e) {
        trackError('Token Decode', e.message);
      }
    }

    window.HubSpotConversations?.widget?.refresh();
    console.log("HubSpot widget refreshed.");
  }, []);

  if (process.env.REACT_APP_DISABLE_CONSOLE === 'true') {
    console.log = () => { };
    console.warn = () => { };
    console.error = () => { };
  }

  return (
    <BrowserRouter forceRefresh={true}>
      <RoleSelectionProvider>
      <TeacherNavigationProvider>
      <ScrollToTop />
      <RouteTracker />
      <MessageModal />
      <Suspense
        fallback={
          <div className="spinner-container">
            <div className="big-ball-spinner"></div>
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<App />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/recovery" element={<Recovery />} />
          {process.env.NODE_ENV === "development" && (
            <>
              <Route
                path="/dev/class-access-flow"
                element={<ClassAccessFlowPreview />}
              />
              <Route
                path="/dev/class-access-message"
                element={(
                  <div className="vh-100 blue-background pt-4">
                    <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                      <ClassAccessDeniedAlert className="m-4" />
                    </div>
                  </div>
                )}
              />
            </>
          )}
          <Route path="/dyslexiascreenersession" element={<DyslexiaScreenerSession />} />
          <Route path="/dyslexiascreenerform" element={<DyslexiaScreenerForm />} />
          <Route path="/sandbox" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.SANDBOX} element={<Sandbox />} />} />
          <Route path="/masterdashboard" element={<ProtectedRoute element={<MasterDashboard />} />} />
          <Route path="/admindashboard" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.ADMIN} element={<AdminDashboard />} />} />
          <Route path="/assessmentsdashboard" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.ASSIGN_SCREENER} element={<AssessmentsDashboard />} />} />
          <Route path="/classadmindashboard" element={<ProtectedRoute element={<ClassAdminDashboard />} />} />
          <Route path="/qrdashboard" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.STUDENT_QRS} element={<QRDashboard />} />} />
          <Route path="/resultsdashboard" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.RESULTS} element={<ResultsDashboard />} />} />
          <Route path="/reporting" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.REPORTING} element={<Reporting />} />} />
          <Route path="/profile" element={<ProtectedRoute element={<TeacherProfile />} />} />
          <Route path="/createclass" element={<ProtectedRoute element={<CreateClass />} />} />
          <Route path="/createspecialedclass" element={<ProtectedRoute element={<CreateSpecialEdClass />} />} />
          <Route path="/createtrialclass" element={<ProtectedRoute element={<CreateTrialClass />} />} />
          <Route path="/createsetclass" element={<ProtectedRoute element={<CreateSETClass />} />} />
          <Route path="/class/detail" element={<ProtectedRoute element={<ClassDetail />} />} />
          <Route path="/assessment/detail" element={<ProtectedRoute element={<AssessmentDetail />} />} />
          <Route path="/results/detail" element={<ProtectedRoute element={<ResultDetail />} />} />
          <Route path="/sencoresults/detail" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.RESULTS} element={<SencoResultDetail />} />} />
          <Route path="/class/share" element={<ProtectedRoute element={<ShareClass />} />} />
          <Route path="/addstudents" element={<ProtectedRoute element={<AddStudents />} />} />
          <Route path="/class/settings" element={<ProtectedRoute element={<ClassSettings />} />} />
          <Route path="/class/settings/special" element={<ProtectedRoute element={<ClassSettingsSpecial />} />} />
          <Route path="/studentsresults" element={<ProtectedRoute element={<StudentsResults />} />} />
          <Route path="/senco/studentsresults" element={<ProtectedRoute element={<SencoStudentResults />} />} />
          <Route path="/student/details" element={<ProtectedRoute element={<StudentDetails />} />} />
          <Route path="/student/settings" element={<ProtectedRoute element={<StudentSettings />} />} />
          <Route path="/student/results" element={<ProtectedRoute element={<IndividualStudentResults />} />} />
          <Route path="/allstudents/results" element={<ProtectedRoute element={<AllStudentResults />} />} />
          <Route path="/allstudents/reportcards" element={<ProtectedRoute element={<AllStudentReportCards />} />} />
          <Route path="/assignassessment" element={<ProtectedRoute element={<AssignAssessment />} />} />
          <Route path="/recommendations" element={<ProtectedRoute element={<Recommendations />} />} />
          <Route path="/results" element={<ProtectedRoute element={<Results />} />} />
          <Route path="/supersecretinternaldash" element={<ProtectedRoute element={<SuperSecretInternalDash />} />} />
          <Route path="/mycolleagues" element={<ProtectedRoute element={<MyColleagues />} />} />
          <Route path="/invitecolleagues" element={<ProtectedRoute element={<InviteColleagues />} />} />
          <Route path="/referfriends" element={<ProtectedRoute element={<ReferFriends />} />} />
          <Route path="/reports" element={<ProtectedRoute element={<Reports />} />} />
          <Route path="/sencoreports" element={<ProtectedRoute element={<SencoReports />} />} />
          <Route path="/senwatchlist" element={<ProtectedRoute element={<SENWatchlist />} />} />
          <Route path="/jisireports" element={<ProtectedRoute element={<JiSiReports />} />} />
          <Route path="/sifcreports" element={<ProtectedRoute element={<SiFcReports />} />} />
          <Route path="/retesttaskdashboard" element={<ProtectedRoute element={<RetestTaskDashboard />} />} />
          <Route path="/assigntask" element={<ProtectedRoute element={<AssignTask />} />} />
          <Route path="/retesttaskdetail" element={<ProtectedRoute element={<RetestTaskDetail />} />} />
          <Route path="/customassessmentsdashboard" element={<ProtectedRoute element={<CustomAssessmentsDashboard />} />} />
          <Route path="/aladdinexport" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.ALADDIN_EXPORT} element={<AladdinExportAvailabilityProvider><AladdinExportAvailabilityGate><AladdinExport /></AladdinExportAvailabilityGate></AladdinExportAvailabilityProvider>} />} />
          <Route path="/senco/handover" element={<ProtectedRoute navigationIds={[TEACHER_DASHBOARD_NAVIGATION_IDS.REPORTING, TEACHER_DASHBOARD_NAVIGATION_IDS.CLASSROOM_HANDOVER]} element={<HandoverDocs />} />} />
          <Route path="/classroom-handover/:classId/setup" element={<ProtectedRoute element={<ClassroomHandoverSetup />} />} />
          <Route path="/classroom-handover/:classId/children" element={<ProtectedRoute element={<ClassroomHandoverChildren />} />} />
          <Route path="/classroom-handover/different-groupings" element={<ProtectedRoute element={<ClassAdminDashboard />} />} />
          <Route path="/classroom-handover/different-groupings/nominate" element={<ProtectedRoute element={<ClassAdminDashboard />} />} />
          <Route path="/beforescreening" element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.BEFORE_SCREENING} element={<BeforeScreening />} />} />
          <Route
            path="/dyslexiascreenerdashboard"
            element={<ProtectedRoute element={<DyslexiaScreenerDashboard />} />}
          />
          <Route
            path="/dyslexiascreeneradmin"
            element={<ProtectedRoute element={<DyslexiaScreenerAdmin />} />}
          />
          <Route
            path="/dyslexiascreenerresults"
            element={<ProtectedRoute element={<DyslexiaScreenerResults />} />}
          />
          <Route path="/rti" element={<ProtectedRoute element={<RTI />} />} />

          <Route
            path="/eoy-report-assistant"
            element={<ProtectedRoute navigationIds={[TEACHER_DASHBOARD_NAVIGATION_IDS.REPORTING, TEACHER_DASHBOARD_NAVIGATION_IDS.EOY_REPORT_ASSISTANT]} element={<EOYReportAssistant />} />}
          />
          <Route
            path="/enhanced-ai-reports"
            element={<ProtectedRoute navigationId={TEACHER_DASHBOARD_NAVIGATION_IDS.REPORTING} element={<EnhancedAIReports />} />}
          />
          {/* SENSAI - POC, not being taken further
          <Route
            path="/sensai"
            element={<ProtectedRoute element={<Sensai />} />}
          />
          */}
        </Routes>
      </Suspense>
      </TeacherNavigationProvider>
      </RoleSelectionProvider>
    </BrowserRouter>
  );
}

root.render(<RootComponent />);

reportWebVitals();
